// Medya Planı Stüdyosu — brief yapıştır, Gemini planı kursun, rakamları elle düzelt,
// logo koy, temiz modda ekran görüntüsünü al.
//
// DİL — plan DOKÜMANI her zaman İNGİLİZCE (kullanıcı kararı, 12 Ağu 2026): bölüm
// başlıkları, tablo başlıkları, rozetler ve Gemini'nin ürettiği tüm metin. Soldaki
// stüdyo kontrolleri (brief, logo, butonlar) Türkçe kaldı — onlar dokümanın parçası
// değil, aracın kendisi. Sayılar da bu yüzden en-US biçiminde (1,234,567.89).
//
// TASARIM — referans /budget/hupalupa-go-kalamis-festivali: numaralı büyük harf bölüm
// başlıkları, pastel KPI şeridi, 3 sütunlu funnel kartları ve platform ikonlu,
// rowspan'li platform × funnel tablosu (her platformun altında ara toplam satırı).
//
// TASARIM KARARI — türev metrikler modelden GELMEZ, burada hesaplanır.
// Backend (routes/mediaPlanStudio.js) Gemini'den yalnızca bütçe + CPM + CPC ister;
// gösterim = bütçe/CPM×1000 ve tıklama = bütçe/CPC bu dosyada türetilir. Böylece
// kullanıcı bir rakamı değiştirdiğinde plan kendi içinde TUTARLI kalır.
//
// BÜTÇE AKIŞI — çift yönlü ve her zaman toplamı korur:
//   kampanya bütçesi değişti  → kanal bütçesi = kampanyaların toplamı (yukarı akar)
//   kanal bütçesi değişti     → kampanyalar oranlarını koruyarak ölçeklenir (aşağı akar)
//   genel toplam değişti      → tüm kanallar ve kampanyalar oranlı ölçeklenir
// Yuvarlama artığı her seferinde en büyük kaleme yazılır; toplam ASLA kaymaz.
//
// DÜZENLEME — contentEditable kullanılıyor (input değil). Sebep: sayfa aynı anda
// hem düzenleme aracı hem sunum çıktısı; input çerçeveleri ekran görüntüsünü bozar.
// React'in contentEditable içeriğini yeniden yazıp imleci sıfırlamaması için metin
// yalnızca odak DIŞINDAYKEN state'ten senkronlanır (bkz. useSyncedText).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { apiFetch } from '../../utils/apiFetch';

const API = 'https://api.glowytics.com/api/media-plan-studio';
const TASLAK_ANAHTARI = 'mediaPlanStudio:taslak:v1';

/* ─────────────────────────── sayı yardımcıları ─────────────────────────── */

const say = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (n, basamak = 0) =>
  say(n).toLocaleString('en-US', { minimumFractionDigits: basamak, maximumFractionDigits: basamak });

// Hem "1,234.56" (en) hem "1.234,56" (tr) yazımını kabul eder — kullanıcı Türkçe
// klavyeden virgüllü, kopyala-yapıştırdan noktalı sayı girebiliyor.
function sayiCoz(metin) {
  if (typeof metin === 'number') return metin;
  const t0 = String(metin ?? '').replace(/[^\d.,-]/g, '').trim();
  if (!t0) return 0;
  let t = t0;
  const sonVirgul = t.lastIndexOf(',');
  const sonNokta = t.lastIndexOf('.');

  if (sonVirgul >= 0 && sonNokta >= 0) {
    // İkisi de varsa SONDAKİ ondalık ayracıdır, diğeri binlik.
    const ondalik = sonVirgul > sonNokta ? ',' : '.';
    const binlik = ondalik === ',' ? '.' : ',';
    t = t.split(binlik).join('').replace(ondalik, '.');
  } else {
    const ayrac = sonVirgul >= 0 ? ',' : sonNokta >= 0 ? '.' : null;
    if (ayrac) {
      const parca = t.split(ayrac);
      const son = parca[parca.length - 1];
      // Tek ayraç + tam 3 hane → binlik ("1,500" ve "1.500" ikisi de 1500).
      // 1-2 hane ya da 3'ten fazla → ondalık ("25.50" → 25.5, "1,5" → 1.5).
      // Kenar durum: "0.125" gibi 3 haneli gerçek ondalık 125 olur; bütçe/CPM
      // alanlarında pratikte görülmüyor ve odakta ham değer gösterildiği için
      // düzeltmesi kolay.
      t = (parca.length > 2 || (son.length === 3 && parca[0].length > 0))
        ? parca.join('')
        : parca.join('.');
    }
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

// Bütçeyi hedefe oranlı dağıt, yuvarlama artığını en büyük kaleme yaz.
function oranliDagit(kalemler, hedefToplam, alan = 'butce') {
  if (!kalemler.length) return kalemler;
  const mevcut = kalemler.reduce((s, k) => s + say(k[alan]), 0);
  if (mevcut <= 0) {
    const pay = Math.floor(hedefToplam / kalemler.length);
    const yeni = kalemler.map((k) => ({ ...k, [alan]: pay }));
    yeni[0] = { ...yeni[0], [alan]: pay + (hedefToplam - pay * kalemler.length) };
    return yeni;
  }
  const oran = hedefToplam / mevcut;
  let toplam = 0;
  const yeni = kalemler.map((k) => {
    const b = Math.round(say(k[alan]) * oran);
    toplam += b;
    return { ...k, [alan]: b };
  });
  const fark = hedefToplam - toplam;
  if (fark !== 0) {
    let enBuyuk = 0;
    yeni.forEach((k, i) => { if (say(k[alan]) > say(yeni[enBuyuk][alan])) enBuyuk = i; });
    yeni[enBuyuk] = { ...yeni[enBuyuk], [alan]: say(yeni[enBuyuk][alan]) + fark };
  }
  return yeni;
}

// Tek kampanyanın türev metrikleri. CPM/CPC 0 ise bölme yok — 0 döner, Infinity değil.
function turet(k) {
  const b = say(k.butce), cpm = say(k.cpm), cpc = say(k.cpc);
  const gosterim = cpm > 0 ? Math.round((b / cpm) * 1000) : 0;
  const tiklama = cpc > 0 ? Math.round(b / cpc) : 0;
  return { gosterim, tiklama, ctr: gosterim > 0 ? (tiklama / gosterim) * 100 : 0 };
}

function kanalToplam(kanal) {
  return (kanal.kampanyalar || []).reduce(
    (a, k) => {
      const t = turet(k);
      return { butce: a.butce + say(k.butce), gosterim: a.gosterim + t.gosterim, tiklama: a.tiklama + t.tiklama };
    },
    { butce: 0, gosterim: 0, tiklama: 0 }
  );
}

/* ─────────────────────────── düzenlenebilir alanlar ─────────────────────── */

// contentEditable içeriğini state ile senkronlar — ama sadece alan odakta DEĞİLKEN.
// Odaktayken yazarsak React her tuşta düğümü yeniden yazar ve imleç başa sıçrar.
function useSyncedText(ref, metin) {
  const odakta = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (el && !odakta.current && el.innerText !== metin) el.innerText = metin ?? '';
  }, [ref, metin]);
  return odakta;
}

function hepsiniSec(el) {
  const aralik = document.createRange();
  aralik.selectNodeContents(el);
  const sec = window.getSelection();
  sec.removeAllRanges();
  sec.addRange(aralik);
}

// `blok` div DEĞİL, display:block olan bir span üretir. Sebep: bu alanlar <p> ve
// <span> içine de gömülüyor; <div> oraya geçersiz iç içe geçme (validateDOMNesting).
function Metin({ value, onChange, className = '', blok = false, kilit = false, ipucu = '' }) {
  const ref = useRef(null);
  const odakta = useSyncedText(ref, value);
  return (
    <span
      ref={ref}
      contentEditable={!kilit}
      suppressContentEditableWarning
      spellCheck={false}
      data-ipucu={ipucu}
      className={`mps-alan ${blok ? 'block' : 'inline-block'} ${className}`}
      onFocus={() => { odakta.current = true; }}
      onBlur={(e) => {
        odakta.current = false;
        const t = e.currentTarget.innerText.replace(/ /g, ' ').replace(/\n{3,}/g, '\n\n').trim();
        if (t !== value) onChange(t);
        else e.currentTarget.innerText = value ?? '';
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.currentTarget.innerText = value ?? ''; e.currentTarget.blur(); }
        if (!blok && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
      }}
    />
  );
}

function Sayi({ value, onChange, basamak = 0, on = '', arka = '', className = '', kilit = false }) {
  const ref = useRef(null);
  const gosterilen = fmt(value, basamak);
  const odakta = useSyncedText(ref, gosterilen);
  return (
    <span className="whitespace-nowrap">
      {on}
      <span
        ref={ref}
        contentEditable={!kilit}
        suppressContentEditableWarning
        spellCheck={false}
        className={`mps-alan mps-sayi inline-block ${className}`}
        onFocus={(e) => {
          odakta.current = true;
          // Odakta ham değer: "150,000" yerine "150000" düzenlemesi daha kolay.
          e.currentTarget.innerText = String(say(value));
          hepsiniSec(e.currentTarget);
        }}
        onBlur={(e) => {
          odakta.current = false;
          const n = sayiCoz(e.currentTarget.innerText);
          e.currentTarget.innerText = fmt(n, basamak);
          if (n !== say(value)) onChange(n);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { e.currentTarget.innerText = gosterilen; e.currentTarget.blur(); }
        }}
      />
      {arka}
    </span>
  );
}

/* ─────────────────────────── platform ikonları ─────────────────────────── */

// Kanal adı serbest metin (Gemini üretiyor, kullanıcı düzenleyebiliyor) — bu yüzden
// eşleme desenle yapılıyor, tam eşitlikle değil. Sıra ÖNEMLİ: "YouTube" Google'dan
// önce denenmeli, yoksa geniş google deseni onu da yakalar.
//
// 🔴 İKON SEÇERKEN: SVG kullanacaksan dosyada `width`/`height` ÖZNİTELİĞİ OLMALI.
// html2canvas yalnızca `viewBox`'ı olan SVG'nin en-boy oranını hesaplayamıyor ve
// PNG çıktısında görseli eziyor — `youtubelogov2.svg` (viewBox var, width yok) bu
// yüzden kırmızı bir lekeye dönüşüyordu, `tiktokv4.svg` (width=512 height=512)
// sorunsuz. Emin değilsen raster (PNG/WEBP) seç.
// ⚠️ `public/pinterest-icon.png` adı PNG ama içeriği HTML — kullanma.
const IKONLAR = [
  { desen: /tiktok/i,                                                     src: '/tiktokv4.svg',         h: 'h-5' },
  { desen: /youtube|ctv|yt\b/i,                                           src: '/youtubelogo.png',      h: 'h-4' },
  { desen: /pinterest/i,                                                  src: '/pinterestlogopng.png', h: 'h-5' },
  { desen: /meta|facebook|instagram|\bfb\b|\big\b/i,                      src: '/6033716.png',          h: 'h-5' },
  { desen: /google|pmax|performance max|shopping|search|gdn|demand gen/i, src: '/google_ads-512.webp',  h: 'h-5' },
];

function PlatformIkon({ ad, className = '' }) {
  const eslesme = IKONLAR.find((i) => i.desen.test(ad || ''));
  if (eslesme) {
    return <img src={eslesme.src} alt="" className={`${eslesme.h} w-auto object-contain flex-shrink-0 ${className}`} />;
  }
  // Bilinmeyen kanal (Programmatic, LinkedIn, X…) — baş harfli rozet.
  return (
    <span className={`inline-flex items-center justify-center h-5 w-5 rounded bg-gray-200 text-gray-600 text-[10px] font-bold flex-shrink-0 ${className}`}>
      {(ad || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

/* ─────────────────────────── görsel eşlemeler ──────────────────────────── */

const RENKLER = {
  mavi:    { cizgi: 'border-sky-200',     zemin: 'bg-sky-50',     yazi: 'text-sky-700',     dolgu: 'bg-sky-500' },
  yesil:   { cizgi: 'border-emerald-200', zemin: 'bg-emerald-50', yazi: 'text-emerald-700', dolgu: 'bg-emerald-500' },
  mor:     { cizgi: 'border-violet-200',  zemin: 'bg-violet-50',  yazi: 'text-violet-700',  dolgu: 'bg-violet-500' },
  turuncu: { cizgi: 'border-orange-200',  zemin: 'bg-orange-50',  yazi: 'text-orange-700',  dolgu: 'bg-orange-500' },
  pembe:   { cizgi: 'border-pink-200',    zemin: 'bg-pink-50',    yazi: 'text-pink-700',    dolgu: 'bg-pink-500' },
  kirmizi: { cizgi: 'border-red-200',     zemin: 'bg-red-50',     yazi: 'text-red-700',     dolgu: 'bg-red-500' },
  gri:     { cizgi: 'border-gray-200',    zemin: 'bg-gray-50',    yazi: 'text-gray-700',    dolgu: 'bg-gray-500' },
};
const renk = (r) => RENKLER[r] || RENKLER.gri;

const FUNNELLER = ['Upper', 'Mid', 'Lower'];
const FUNNEL_STIL = {
  Upper: { rozet: 'bg-violet-100 text-violet-700 border-violet-200', cizgi: 'border-violet-200', yazi: 'text-violet-700', baslik: 'Awareness & Reach' },
  Mid:   { rozet: 'bg-amber-100 text-amber-700 border-amber-200',    cizgi: 'border-amber-200',  yazi: 'text-amber-700',  baslik: 'Consideration & Engagement' },
  Lower: { rozet: 'bg-emerald-100 text-emerald-700 border-emerald-200', cizgi: 'border-emerald-200', yazi: 'text-emerald-700', baslik: 'Conversion' },
};
const funnelOf = (k) => (FUNNELLER.includes(k?.funnel) ? k.funnel : 'Mid');

const AMAC_STIL = {
  Awareness: 'bg-red-50 text-red-700 border-red-200',
  Engagement: 'bg-pink-50 text-pink-700 border-pink-200',
  Traffic: 'bg-sky-50 text-sky-700 border-sky-200',
  Conversion: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Messages / DM': 'bg-violet-50 text-violet-700 border-violet-200',
  'App Installs': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Video Views': 'bg-amber-50 text-amber-700 border-amber-200',
  Retargeting: 'bg-teal-50 text-teal-700 border-teal-200',
};

const KAYNAK_STIL = {
  'account data': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  brief: 'bg-sky-50 text-sky-700 border-sky-200',
};

const BOS_KAMPANYA = {
  ad: 'New campaign', amac: 'Conversion', funnel: 'Lower', kitle: 'Audience',
  format: 'Format', butce: 0, cpm: 0, cpc: 0, not: '',
};

/* ─────────────────────────── ana bileşen ───────────────────────────────── */

export default function MediaPlanStudio() {
  const [form, setForm] = useState({
    brief: '', brandId: '', gercekVeriKullan: true, markaAdi: '',
    donem: '', toplamButce: 0, paraBirimi: '₺', aySayisi: 12,
  });
  const [markalar, setMarkalar] = useState([]);
  const [plan, setPlan] = useState(null);
  const [veriTemeli, setVeriTemeli] = useState(null);
  const [uretim, setUretim] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const [temizMod, setTemizMod] = useState(false);
  const [logo, setLogo] = useState(null);
  const [indiriliyor, setIndiriliyor] = useState(false);
  const [gecenSure, setGecenSure] = useState(0);

  const planRef = useRef(null);

  /* ── taslak: sayfa yenilenince kaybolmasın ── */
  useEffect(() => {
    try {
      const ham = localStorage.getItem(TASLAK_ANAHTARI);
      if (!ham) return;
      const t = JSON.parse(ham);
      if (t.form) setForm((f) => ({ ...f, ...t.form }));
      if (t.plan) setPlan(t.plan);
      if (t.veriTemeli) setVeriTemeli(t.veriTemeli);
      if (t.uretim) setUretim(t.uretim);
      if (t.logo) setLogo(t.logo);
    } catch { /* bozuk taslak sayfayı düşürmesin */ }
  }, []);

  useEffect(() => {
    const z = setTimeout(() => {
      try {
        localStorage.setItem(TASLAK_ANAHTARI, JSON.stringify({ form, plan, veriTemeli, uretim, logo }));
      } catch { /* kota dolu olabilir — sessiz geç, sayfa çalışmaya devam etsin */ }
    }, 600);
    return () => clearTimeout(z);
  }, [form, plan, veriTemeli, uretim, logo]);

  /* ── marka listesi — backend zaten kullanıcının erişebildikleriyle sınırlıyor ── */
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`${API}/brands`);
        if (!r.ok) return;
        const d = await r.json();
        setMarkalar(d.brands || []);
      } catch { /* seçici boş kalır, benchmark modu yine çalışır */ }
    })();
  }, []);

  useEffect(() => {
    if (!yukleniyor) return;
    setGecenSure(0);
    const s = setInterval(() => setGecenSure((x) => x + 1), 1000);
    return () => clearInterval(s);
  }, [yukleniyor]);

  const olustur = async () => {
    if (form.brief.trim().length < 20) { setHata('Brief en az 20 karakter olmalı.'); return; }
    setYukleniyor(true); setHata('');
    try {
      const r = await apiFetch(`${API}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brief: form.brief, brandId: form.brandId || null,
          gercekVeriKullan: form.gercekVeriKullan, markaAdi: form.markaAdi,
          donem: form.donem, toplamButce: form.toplamButce || null,
          paraBirimi: form.paraBirimi, aySayisi: form.aySayisi,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `Sunucu ${r.status} döndü`);
      setPlan(d.plan); setVeriTemeli(d.veriTemeli); setUretim(d.uretim);
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  };

  /* ── logo: büyük dosya localStorage kotasını patlatmasın, 600px'e küçült ── */
  const logoYukle = (dosya) => {
    if (!dosya) return;
    const okuyucu = new FileReader();
    okuyucu.onload = () => {
      const img = new Image();
      img.onload = () => {
        const enBuyuk = 600;
        const oran = Math.min(1, enBuyuk / Math.max(img.width, img.height));
        if (oran === 1 && String(okuyucu.result).length < 400000) {
          setLogo({ src: okuyucu.result, koyuZemin: false, boyut: 40 });
          return;
        }
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * oran);
        c.height = Math.round(img.height * oran);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        setLogo({ src: c.toDataURL('image/png'), koyuZemin: false, boyut: 40 });
      };
      img.onerror = () => setLogo({ src: okuyucu.result, koyuZemin: false, boyut: 40 });
      img.src = okuyucu.result;
    };
    okuyucu.readAsDataURL(dosya);
  };

  /* ── PNG: yakalarken düzenleme izlerini gizle, sonra eski hale dön ── */
  const pngIndir = async () => {
    if (!planRef.current) return;
    const eskiTemiz = temizMod;
    setIndiriliyor(true);
    setTemizMod(true);
    await new Promise((r) => setTimeout(r, 300)); // stil değişiminin boyanmasını bekle
    try {
      const tuval = await html2canvas(planRef.current, {
        scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
        imageTimeout: 15000,
        windowWidth: planRef.current.scrollWidth,
      });
      const a = document.createElement('a');
      const ad = (plan?.baslik || 'media-plan').replace(/[^\w -]/g, '').replace(/\s+/g, '-').toLowerCase();
      a.download = `${ad}.png`;
      a.href = tuval.toDataURL('image/png');
      a.click();
    } catch (e) {
      setHata(`PNG alınamadı: ${e.message}`);
    } finally {
      setTemizMod(eskiTemiz);
      setIndiriliyor(false);
    }
  };

  /* ── plan güncelleyiciler ── */
  const guncelle = useCallback((fn) => setPlan((p) => (p ? fn(structuredClone(p)) : p)), []);

  const kanalGuncelle = (ki, alan, deger) => guncelle((p) => { p.kanallar[ki][alan] = deger; return p; });

  // Kanal bütçesi elle değişti → kampanyalar oranını koruyarak ölçeklensin.
  const kanalButcesi = (ki, yeni) => guncelle((p) => {
    p.kanallar[ki].butce = yeni;
    p.kanallar[ki].kampanyalar = oranliDagit(p.kanallar[ki].kampanyalar, yeni);
    return p;
  });

  // Kampanya değişti → kanal bütçesi kampanyaların toplamına eşitlensin.
  const kampanyaGuncelle = (ki, kj, alan, deger) => guncelle((p) => {
    p.kanallar[ki].kampanyalar[kj][alan] = deger;
    if (alan === 'butce') {
      p.kanallar[ki].butce = p.kanallar[ki].kampanyalar.reduce((s, c) => s + say(c.butce), 0);
    }
    return p;
  });

  const kampanyaEkle = (ki) => guncelle((p) => { p.kanallar[ki].kampanyalar.push({ ...BOS_KAMPANYA }); return p; });

  const kampanyaSil = (ki, kj) => guncelle((p) => {
    p.kanallar[ki].kampanyalar.splice(kj, 1);
    p.kanallar[ki].butce = p.kanallar[ki].kampanyalar.reduce((s, c) => s + say(c.butce), 0);
    return p;
  });

  const kanalEkle = () => guncelle((p) => {
    p.kanallar.push({ ad: 'New Channel', renk: 'gri', butce: 0, not: '', kampanyalar: [{ ...BOS_KAMPANYA }] });
    return p;
  });

  const kanalSil = (ki) => guncelle((p) => { p.kanallar.splice(ki, 1); return p; });

  // Genel toplam elle değişti → her şey oranlı ölçeklensin.
  const genelToplamDegis = (yeni) => guncelle((p) => {
    p.kanallar = oranliDagit(p.kanallar, yeni).map((k) => ({
      ...k, kampanyalar: oranliDagit(k.kampanyalar, say(k.butce)),
    }));
    return p;
  });

  const listeGuncelle = (yol, i, alan, deger) => guncelle((p) => { p[yol][i][alan] = deger; return p; });

  /* ── toplamlar ── */
  const ozet = useMemo(() => {
    if (!plan?.kanallar) return { butce: 0, gosterim: 0, tiklama: 0 };
    return plan.kanallar.reduce((a, k) => {
      const t = kanalToplam(k);
      return { butce: a.butce + t.butce, gosterim: a.gosterim + t.gosterim, tiklama: a.tiklama + t.tiklama };
    }, { butce: 0, gosterim: 0, tiklama: 0 });
  }, [plan]);

  // Funnel özeti kampanyalardan HESAPLANIYOR, modelden ayrıca istenmiyor — bütçeyi
  // elle değiştirince funnel kartları da anında doğru kalsın diye.
  const funnelOzet = useMemo(() => {
    const g = Object.fromEntries(FUNNELLER.map((f) => [f, { butce: 0, gosterim: 0, tiklama: 0, taktikler: [] }]));
    (plan?.kanallar || []).forEach((k) => (k.kampanyalar || []).forEach((c) => {
      const f = funnelOf(c);
      const t = turet(c);
      g[f].butce += say(c.butce);
      g[f].gosterim += t.gosterim;
      g[f].tiklama += t.tiklama;
      g[f].taktikler.push({ kanal: k.ad, kampanya: c.ad });
    }));
    return g;
  }, [plan]);

  const pb = form.paraBirimi || '₺';
  const kilit = temizMod;

  return (
    <div className={`min-h-screen bg-gray-50 ${temizMod ? 'mps-temiz' : ''}`}>
      <StilBlogu />

      {!temizMod && (
        <div className="sticky top-0 z-30 bg-white border-b shadow-sm">
          <div className="max-w-[1700px] mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
            <div>
              <h1 className="text-base font-bold text-gray-800">Medya Planı Stüdyosu</h1>
              <p className="text-[11px] text-gray-500">Brief yapıştır → Gemini planı kursun → rakamları düzelt → ekran görüntüsünü al</p>
            </div>
            <div className="flex-1" />
            {plan && (
              <>
                <button onClick={() => setTemizMod(true)} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Temiz mod
                </button>
                <button onClick={pngIndir} disabled={indiriliyor}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">
                  {indiriliyor ? 'Hazırlanıyor…' : 'PNG indir'}
                </button>
                <button
                  onClick={() => { if (window.confirm('Plan ve taslak silinecek. Emin misin?')) { setPlan(null); setVeriTemeli(null); setUretim(null); localStorage.removeItem(TASLAK_ANAHTARI); } }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
                  Sıfırla
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {temizMod && !indiriliyor && (
        // Yarı saydam duruyor: PNG dışa aktarımında zaten gizleniyor ama kullanıcı
        // işletim sisteminin ekran görüntüsünü alırsa da göze batmasın.
        <button onClick={() => setTemizMod(false)}
          className="fixed bottom-5 right-5 z-50 px-3 py-2 text-xs font-medium rounded-lg bg-gray-900 text-white shadow-lg opacity-25 hover:opacity-100 transition-opacity">
          Düzenlemeye dön
        </button>
      )}

      <div className={`max-w-[1700px] mx-auto ${temizMod ? 'p-0' : 'px-6 py-6'} grid gap-6 ${temizMod ? '' : 'lg:grid-cols-[360px_minmax(0,1fr)]'}`}>

        {/* ── sol panel (stüdyo kontrolleri — Türkçe, dokümanın parçası değil) ── */}
        {!temizMod && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border shadow-sm p-4 space-y-3">
              <label className="block">
                <span className="text-xs font-semibold text-gray-700">Brief</span>
                <textarea
                  value={form.brief}
                  onChange={(e) => setForm({ ...form, brief: e.target.value })}
                  rows={9}
                  placeholder={'Brief\'i Türkçe yazabilirsin — plan her zaman İngilizce çıkar.\n\nÖrn: Hupalupa Store için Eylül 2026 planı. Okula dönüş bitti, sonbahar oyuncak sezonuna giriyoruz. Odak yeni lisanslı ürün serisi. Meta ağırlıklı, katalog kampanyaları önemli.'}
                  className="mt-1 w-full text-xs border rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none resize-y"
                />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs font-semibold text-gray-700">Dönem</span>
                  <input value={form.donem} onChange={(e) => setForm({ ...form, donem: e.target.value })}
                    placeholder="September 2026"
                    className="mt-1 w-full text-xs border rounded-lg px-2.5 py-2 outline-none focus:border-indigo-400" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-gray-700">Toplam bütçe</span>
                  <input value={form.toplamButce ? fmt(form.toplamButce) : ''}
                    onChange={(e) => setForm({ ...form, toplamButce: sayiCoz(e.target.value) })}
                    placeholder="450,000"
                    className="mt-1 w-full text-xs border rounded-lg px-2.5 py-2 outline-none focus:border-indigo-400" />
                </label>
              </div>

              <label className="block">
                <span className="text-xs font-semibold text-gray-700">Marka <span className="font-normal text-gray-400">(opsiyonel)</span></span>
                <select value={form.brandId} onChange={(e) => {
                  const m = markalar.find((x) => x.id === e.target.value);
                  setForm({ ...form, brandId: e.target.value, markaAdi: m?.name || form.markaAdi });
                }}
                  className="mt-1 w-full text-xs border rounded-lg px-2.5 py-2 outline-none focus:border-indigo-400 bg-white">
                  <option value="">— seçilmedi (pazar tahmini) —</option>
                  {markalar.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <span className="text-[10px] text-gray-400">Yalnızca erişimin olan markalar listeleniyor.</span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.gercekVeriKullan}
                  onChange={(e) => setForm({ ...form, gercekVeriKullan: e.target.checked })}
                  className="mt-0.5" />
                <span className="text-[11px] text-gray-600 leading-snug">
                  <b className="text-gray-800">Gerçek veriyi kullan.</b> Seçili markanın son {form.aySayisi} aylık
                  gerçekleşen CPM/CPC/CTR'ı ve aylık mevsimsellik endeksi Gemini'ye verilir.
                  Veri yoksa pazar tahminine düşer ve <b>sayfada bunu yazar</b>.
                </span>
              </label>

              <button onClick={olustur} disabled={yukleniyor}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60">
                {yukleniyor ? `Gemini planı kuruyor… ${gecenSure}sn` : plan ? 'Yeniden oluştur' : 'Planı oluştur'}
              </button>
              {yukleniyor && <p className="text-[11px] text-gray-500 text-center">Bu adım genelde 40-70 saniye sürüyor.</p>}
              {hata && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{hata}</div>}
            </div>

            <div className="bg-white rounded-2xl border shadow-sm p-4 space-y-3">
              <span className="text-xs font-semibold text-gray-700">Logo</span>
              <input type="file" accept="image/*" onChange={(e) => logoYukle(e.target.files?.[0])}
                className="block w-full text-[11px] file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[11px] file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200" />
              {logo && (
                <>
                  <div className={`rounded-lg p-3 flex items-center justify-center ${logo.koyuZemin ? 'bg-gray-900' : 'bg-gray-50 border'}`}>
                    <img src={logo.src} alt="logo" style={{ height: logo.boyut }} className="w-auto object-contain" />
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={logo.koyuZemin}
                      onChange={(e) => setLogo({ ...logo, koyuZemin: e.target.checked })} />
                    Koyu zemin <span className="text-gray-400">(beyaz logolar için)</span>
                  </label>
                  <label className="block text-[11px] text-gray-600">
                    Boyut: {logo.boyut}px
                    <input type="range" min="20" max="90" value={logo.boyut}
                      onChange={(e) => setLogo({ ...logo, boyut: Number(e.target.value) })} className="w-full" />
                  </label>
                  <button onClick={() => setLogo(null)} className="text-[11px] text-red-600 hover:underline">Logoyu kaldır</button>
                </>
              )}
            </div>

            {veriTemeli?.platformlar && (
              <div className="bg-white rounded-2xl border shadow-sm p-4">
                <span className="text-xs font-semibold text-gray-700">Planı besleyen gerçek veri</span>
                <div className="mt-2 space-y-2">
                  {Object.entries(veriTemeli.platformlar).map(([k, p]) => (
                    <div key={k} className="text-[11px] border rounded-lg p-2">
                      <div className="font-semibold text-gray-800 flex items-center gap-1.5">
                        <PlatformIkon ad={p.etiket} /> {p.etiket}
                      </div>
                      {p.veriVar ? (
                        <>
                          <div className="text-gray-600">CPM ₺{fmt(p.cpm, 2)} · CPC ₺{fmt(p.cpc, 2)} · CTR {fmt(p.ctr, 2)}%</div>
                          <div className="text-gray-500">ROAS {fmt(p.roas, 2)}× · harcama ₺{fmt(p.harcama)}</div>
                          <div className="text-gray-400">{p.kapsama.ilkTarih} → {p.kapsama.sonTarih} ({p.kapsama.veriGunu} gün)</div>
                        </>
                      ) : (
                        <div className="text-red-600">
                          veri yok ({p.sebep === 'tablo_yok' ? 'tablo kurulmamış' : p.sebep === 'veri_yok' ? 'kayıt yok' : 'hata'})
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uretim && (
              <p className="text-[10px] text-gray-400 px-1">
                {uretim.model} · {Math.round(uretim.sureMs / 1000)} sn · {new Date(uretim.uretimZamani).toLocaleString('tr-TR')}
                <br />Değişiklikler tarayıcında saklanır, sunucuya yazılmaz.
              </p>
            )}
          </div>
        )}

        {/* ── plan dokümanı (İngilizce) ── */}
        <div>
          {!plan && !yukleniyor && (
            <div className="bg-white rounded-2xl border shadow-sm p-12 text-center">
              <p className="text-sm text-gray-500">Soldaki brief'i doldurup <b>Planı oluştur</b>'a bas.</p>
              <p className="text-xs text-gray-400 mt-2">Plan geldikten sonra sayfadaki <b>her rakam ve her metin</b> tıklanıp değiştirilebilir.</p>
            </div>
          )}

          {plan && (
            <div ref={planRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">

              {/* başlık */}
              <div className="border-b p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4 min-w-0">
                  {logo && (
                    <div className={`rounded-lg flex-shrink-0 ${logo.koyuZemin ? 'bg-gray-900 px-3 py-2' : ''}`}>
                      <img src={logo.src} alt="" style={{ height: logo.boyut }} className="w-auto object-contain" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-800">
                      <Metin value={plan.baslik} onChange={(v) => guncelle((p) => { p.baslik = v; return p; })} kilit={kilit} />
                    </h2>
                    <p className="text-sm text-gray-500">
                      <Metin value={plan.altBaslik} onChange={(v) => guncelle((p) => { p.altBaslik = v; return p; })} kilit={kilit} />
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {form.donem && (
                    <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border">
                      {form.donem}
                    </span>
                  )}
                  <span className="inline-flex items-center px-3 py-1.5 bg-gray-900 text-white text-sm font-semibold rounded-full">
                    {/* Sabit boşluk: normal boşluk etiketle sayı arasında daralıp
                        "Budget:₺450,000" gibi bitişik çıkıyordu. */}
                    {'Budget: '}<Sayi value={ozet.butce} onChange={genelToplamDegis} on={pb} kilit={kilit} className="mps-koyu" />
                  </span>
                  {/* Hedef bütçeden sapma sessiz kalmasın — tek tek düzenlerken toplamın
                      kaydığını fark etmemek bu sayfanın en kolay hatası. */}
                  {form.toplamButce > 0 && ozet.butce !== form.toplamButce && (
                    <span className="inline-block align-middle px-2.5 py-1 bg-amber-100 text-amber-800 border border-amber-300 text-[11px] font-semibold rounded-full whitespace-nowrap">
                      target {pb}{fmt(form.toplamButce)} · {ozet.butce > form.toplamButce ? '+' : '−'}{pb}{fmt(Math.abs(ozet.butce - form.toplamButce))}
                    </span>
                  )}
                </div>
              </div>

              {/* veri temeli — planın hangi zeminde durduğu her zaman görünür */}
              {veriTemeli && (
                <div className={`px-5 py-2.5 text-[11px] border-b flex items-start gap-2 ${
                  veriTemeli.mode === 'gercek'
                    ? 'bg-emerald-50 border-emerald-100 text-emerald-800'
                    : 'bg-amber-50 border-amber-100 text-amber-800'}`}>
                  <span className="font-bold whitespace-nowrap">
                    {veriTemeli.mode === 'gercek' ? 'ACCOUNT DATA' : 'MARKET ESTIMATE'}
                  </span>
                  <span className="leading-snug">{veriTemeli.veriNotu}</span>
                </div>
              )}

              <div className="p-6 space-y-9">

                {/* ── 1. OBJECTIVE & BUDGET SUMMARY ── */}
                <Bolum no={1} baslik="Objective & Budget Summary">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <Kpi renk="slate"   etiket="Total Budget" deger={`${pb}${fmt(ozet.butce)}`} alt={form.donem} />
                    <Kpi renk="violet"  etiket="Impressions"  deger={kisalt(ozet.gosterim)} alt="budget ÷ CPM × 1,000" />
                    <Kpi renk="sky"     etiket="Clicks"       deger={kisalt(ozet.tiklama)} alt="budget ÷ CPC" />
                    <Kpi renk="emerald" etiket="Blended CPM"  deger={`${pb}${fmt(ozet.gosterim > 0 ? (ozet.butce / ozet.gosterim) * 1000 : 0, 2)}`} alt="derived" />
                    <Kpi renk="amber"   etiket="Blended CPC"  deger={`${pb}${fmt(ozet.tiklama > 0 ? ozet.butce / ozet.tiklama : 0, 2)}`} alt="derived" />
                    <Kpi renk="rose"    etiket="Blended CTR"  deger={`${fmt(ozet.gosterim > 0 ? (ozet.tiklama / ozet.gosterim) * 100 : 0, 2)}%`} alt="clicks ÷ impressions" />
                  </div>

                  <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200">
                    <p className="text-xs font-semibold text-indigo-900 mb-1">
                      <Metin value={plan.odakBaslik} onChange={(v) => guncelle((p) => { p.odakBaslik = v; return p; })} kilit={kilit} />
                    </p>
                    <p className="text-[11px] text-indigo-800 leading-relaxed">
                      <Metin blok value={plan.odakMetin} onChange={(v) => guncelle((p) => { p.odakMetin = v; return p; })} kilit={kilit} />
                    </p>
                  </div>

                  {/* bütçe dağılım şeridi */}
                  <div>
                    <div className="flex h-3 rounded-full overflow-hidden border">
                      {plan.kanallar.map((k, i) => {
                        const pay = ozet.butce > 0 ? (kanalToplam(k).butce / ozet.butce) * 100 : 0;
                        return <div key={i} className={renk(k.renk).dolgu} style={{ width: `${pay}%` }} />;
                      })}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                      {plan.kanallar.map((k, i) => {
                        const t = kanalToplam(k);
                        const pay = ozet.butce > 0 ? (t.butce / ozet.butce) * 100 : 0;
                        return (
                          <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                            <span className={`w-2.5 h-2.5 rounded-sm ${renk(k.renk).dolgu}`} />
                            <PlatformIkon ad={k.ad} />
                            {k.ad} — {pb}{fmt(t.butce)} <span className="text-gray-400">({pay.toFixed(1)}%)</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </Bolum>

                {/* ── 2. FULL-FUNNEL SPLIT ── */}
                <Bolum no={2} baslik="Full-Funnel Budget Split">
                  <div className="grid gap-3 md:grid-cols-3">
                    {FUNNELLER.map((f) => {
                      const g = funnelOzet[f];
                      const s = FUNNEL_STIL[f];
                      const pay = ozet.butce > 0 ? (g.butce / ozet.butce) * 100 : 0;
                      return (
                        <div key={f} className={`rounded-xl border-2 ${s.cizgi} bg-white p-4 flex flex-col`}>
                          <span className={`self-start px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${s.rozet}`}>
                            {f} Funnel
                          </span>
                          <h4 className="mt-2 text-sm font-bold text-gray-800">{s.baslik}</h4>
                          <div className={`mt-2 text-2xl font-bold ${s.yazi}`}>{pb}{fmt(g.butce)}</div>
                          <div className={`text-[11px] ${s.yazi}`}>
                            {pay.toFixed(1)}% of budget · {kisalt(g.gosterim)} impr · {kisalt(g.tiklama)} clicks
                          </div>
                          <ul className="mt-3 space-y-1 flex-1">
                            {g.taktikler.length === 0 && (
                              <li className="text-[11px] text-gray-400">No campaigns in this layer.</li>
                            )}
                            {g.taktikler.map((t, i) => (
                              <li key={i} className="text-[11px] text-gray-600 flex gap-1.5">
                                <span className="text-gray-300 select-none">•</span>
                                <span>{t.kampanya}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </Bolum>

                {/* ── 3. CAMPAIGN PLAN ── */}
                <Bolum no={3} baslik="Campaign Plan — Platform × Funnel">
                  <div className="border rounded-xl overflow-hidden overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                        <tr>
                          <th className="text-left font-semibold px-3 py-2.5 w-[150px]">Platform</th>
                          <th className="text-left font-semibold px-3 py-2.5">Campaign</th>
                          <th className="text-left font-semibold px-3 py-2.5">Funnel</th>
                          <th className="text-left font-semibold px-3 py-2.5">Objective</th>
                          <th className="text-right font-semibold px-3 py-2.5">CPM / CPC</th>
                          <th className="text-right font-semibold px-3 py-2.5 bg-gray-100">Impressions</th>
                          <th className="text-right font-semibold px-3 py-2.5 bg-gray-100">Clicks</th>
                          <th className="text-right font-semibold px-3 py-2.5 bg-gray-100">CTR</th>
                          <th className="text-right font-semibold px-3 py-2.5">Budget</th>
                          {!kilit && <th className="w-7" />}
                        </tr>
                      </thead>

                      {plan.kanallar.map((kanal, ki) => {
                        const c = renk(kanal.renk);
                        const t = kanalToplam(kanal);
                        const n = kanal.kampanyalar.length || 1;
                        return (
                          <tbody key={ki} className="border-t">
                            {kanal.kampanyalar.map((kmp, kj) => {
                              const d = turet(kmp);
                              const fs = FUNNEL_STIL[funnelOf(kmp)];
                              return (
                                <tr key={kj} className="align-top border-b last:border-b-0 hover:bg-gray-50/60">
                                  {/* Platform hücresi rowspan ile birleşik — referans plandaki görünüm */}
                                  {kj === 0 && (
                                    <td rowSpan={n} className={`px-3 py-3 align-middle border-r ${c.zemin}`}>
                                      <div className="flex items-center gap-2">
                                        <PlatformIkon ad={kanal.ad} />
                                        <span className={`font-bold text-[12px] ${c.yazi}`}>
                                          <Metin value={kanal.ad} onChange={(v) => kanalGuncelle(ki, 'ad', v)} kilit={kilit} />
                                        </span>
                                      </div>
                                      <div className="mt-1 text-[10px] text-gray-500 leading-snug">
                                        <Metin blok value={kanal.not} onChange={(v) => kanalGuncelle(ki, 'not', v)} kilit={kilit} />
                                      </div>
                                      {!kilit && (
                                        <button onClick={() => kanalSil(ki)}
                                          className="mps-arac mt-1.5 text-[10px] px-1.5 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50">
                                          remove channel
                                        </button>
                                      )}
                                    </td>
                                  )}

                                  <td className="px-3 py-2.5">
                                    <div className="font-medium text-gray-800">
                                      <Metin value={kmp.ad} onChange={(v) => kampanyaGuncelle(ki, kj, 'ad', v)} kilit={kilit} />
                                    </div>
                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                      <Metin value={kmp.kitle} onChange={(v) => kampanyaGuncelle(ki, kj, 'kitle', v)} kilit={kilit} />
                                      <span className="text-gray-300 mx-1">·</span>
                                      <Metin value={kmp.format} onChange={(v) => kampanyaGuncelle(ki, kj, 'format', v)} kilit={kilit} />
                                    </div>
                                    {(kmp.not || !kilit) && (
                                      <div className="text-[10px] text-gray-400 mt-0.5 max-w-[340px]">
                                        <Metin blok value={kmp.not} onChange={(v) => kampanyaGuncelle(ki, kj, 'not', v)} kilit={kilit} />
                                      </div>
                                    )}
                                  </td>

                                  <td className="px-3 py-2.5">
                                    <span className={`inline-block align-middle px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${fs.rozet}`}>
                                      <Metin value={funnelOf(kmp)} onChange={(v) => kampanyaGuncelle(ki, kj, 'funnel', v)} kilit={kilit} />
                                    </span>
                                  </td>

                                  <td className="px-3 py-2.5">
                                    {/* inline-block, inline-flex DEĞİL: html2canvas, tek çocuğu
                                        inline-block olan bir inline-flex kutusunun metnini
                                        düşürüyor — rozet PNG'de boş çıkıyordu. */}
                                    <span className={`inline-block align-middle px-2 py-0.5 rounded-md border text-[10px] font-medium whitespace-nowrap ${AMAC_STIL[kmp.amac] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                      <Metin value={kmp.amac} onChange={(v) => kampanyaGuncelle(ki, kj, 'amac', v)} kilit={kilit} />
                                    </span>
                                  </td>

                                  <td className="px-3 py-2.5 text-right text-gray-700 whitespace-nowrap">
                                    <div><Sayi value={kmp.cpm} basamak={2} onChange={(v) => kampanyaGuncelle(ki, kj, 'cpm', v)} on={pb} kilit={kilit} /></div>
                                    <div className="text-gray-500"><Sayi value={kmp.cpc} basamak={2} onChange={(v) => kampanyaGuncelle(ki, kj, 'cpc', v)} on={pb} kilit={kilit} /></div>
                                  </td>

                                  <td className="px-3 py-2.5 text-right text-gray-600 bg-gray-50/70">{fmt(d.gosterim)}</td>
                                  <td className="px-3 py-2.5 text-right text-gray-600 bg-gray-50/70">{fmt(d.tiklama)}</td>
                                  <td className="px-3 py-2.5 text-right text-gray-600 bg-gray-50/70">{fmt(d.ctr, 2)}%</td>

                                  <td className="px-3 py-2.5 text-right font-bold text-gray-800">
                                    <Sayi value={kmp.butce} onChange={(v) => kampanyaGuncelle(ki, kj, 'butce', v)} on={pb} kilit={kilit} />
                                  </td>

                                  {!kilit && (
                                    <td className="px-1 py-2.5 text-center">
                                      <button onClick={() => kampanyaSil(ki, kj)} title="Satırı sil"
                                        className="mps-arac text-gray-300 hover:text-red-600 text-sm leading-none">×</button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}

                            {/* platform ara toplamı — referanstaki tonlu satır */}
                            <tr className={`${c.zemin} font-bold ${c.yazi}`}>
                              <td className={`px-3 py-2 border-r ${c.zemin}`} />
                              <td className="px-3 py-2" colSpan={3}>{kanal.ad} Total</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {pb}{fmt(t.gosterim > 0 ? (t.butce / t.gosterim) * 1000 : 0, 2)} / {pb}{fmt(t.tiklama > 0 ? t.butce / t.tiklama : 0, 2)}
                              </td>
                              <td className="px-3 py-2 text-right">{fmt(t.gosterim)}</td>
                              <td className="px-3 py-2 text-right">{fmt(t.tiklama)}</td>
                              <td className="px-3 py-2 text-right">{fmt(t.gosterim > 0 ? (t.tiklama / t.gosterim) * 100 : 0, 2)}%</td>
                              <td className="px-3 py-2 text-right">
                                <Sayi value={t.butce} onChange={(v) => kanalButcesi(ki, v)} on={pb} kilit={kilit} />
                              </td>
                              {!kilit && (
                                <td className="px-1 py-2 text-center">
                                  <button onClick={() => kampanyaEkle(ki)} title="Kampanya ekle"
                                    className="mps-arac text-indigo-400 hover:text-indigo-700 text-sm leading-none">+</button>
                                </td>
                              )}
                            </tr>
                          </tbody>
                        );
                      })}

                      <tfoot className="bg-gray-900 text-white font-bold">
                        <tr>
                          <td className="px-3 py-2.5" colSpan={4}>Grand Total</td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {pb}{fmt(ozet.gosterim > 0 ? (ozet.butce / ozet.gosterim) * 1000 : 0, 2)} / {pb}{fmt(ozet.tiklama > 0 ? ozet.butce / ozet.tiklama : 0, 2)}
                          </td>
                          <td className="px-3 py-2.5 text-right">{fmt(ozet.gosterim)}</td>
                          <td className="px-3 py-2.5 text-right">{fmt(ozet.tiklama)}</td>
                          <td className="px-3 py-2.5 text-right">{fmt(ozet.gosterim > 0 ? (ozet.tiklama / ozet.gosterim) * 100 : 0, 2)}%</td>
                          <td className="px-3 py-2.5 text-right">{pb}{fmt(ozet.butce)}</td>
                          {!kilit && <td />}
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {!kilit && (
                    <button onClick={kanalEkle} className="mps-arac text-[11px] text-indigo-600 hover:underline">+ add channel</button>
                  )}
                </Bolum>

                {/* ── 4. MONTHLY SPLIT ── */}
                {plan.aylikDagilim?.length > 0 && (
                  <Bolum no={4} baslik="Monthly Split">
                    <div className="border rounded-xl overflow-hidden overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                          <tr>
                            <th className="text-left font-semibold px-3 py-2.5">Period</th>
                            <th className="text-right font-semibold px-3 py-2.5">Budget</th>
                            <th className="text-left font-semibold px-3 py-2.5">Note</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {plan.aylikDagilim.map((a, i) => (
                            <tr key={i} className="align-top">
                              <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                                <Metin value={a.donem} onChange={(v) => listeGuncelle('aylikDagilim', i, 'donem', v)} kilit={kilit} />
                              </td>
                              <td className="px-3 py-2 text-right font-semibold">
                                <Sayi value={a.butce} onChange={(v) => listeGuncelle('aylikDagilim', i, 'butce', v)} on={pb} kilit={kilit} />
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                <Metin blok value={a.not} onChange={(v) => listeGuncelle('aylikDagilim', i, 'not', v)} kilit={kilit} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50 font-semibold text-gray-700">
                          <tr>
                            <td className="px-3 py-2">Total</td>
                            <td className="px-3 py-2 text-right">{pb}{fmt(plan.aylikDagilim.reduce((s, a) => s + say(a.butce), 0))}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </Bolum>
                )}

                {/* ── 5. PROJECTION ── */}
                <Bolum no={plan.aylikDagilim?.length > 0 ? 5 : 4} baslik="Outcome Projection">
                  <Projeksiyon plan={plan} ozet={ozet} pb={pb} kilit={kilit} guncelle={guncelle} />
                </Bolum>

                {/* ── 6. ASSUMPTIONS ── */}
                {plan.varsayimlar?.length > 0 && (
                  <Bolum no={plan.aylikDagilim?.length > 0 ? 6 : 5} baslik="Assumptions & Sources">
                    <div className="border rounded-xl overflow-hidden overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                          <tr>
                            <th className="text-left font-semibold px-3 py-2.5 w-[22%]">Assumption</th>
                            <th className="text-left font-semibold px-3 py-2.5 w-[18%]">Value</th>
                            <th className="text-left font-semibold px-3 py-2.5 w-[14%]">Source</th>
                            <th className="text-left font-semibold px-3 py-2.5">Note</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {plan.varsayimlar.map((v, i) => (
                            <tr key={i} className="align-top">
                              <td className="px-3 py-2 font-medium text-gray-800">
                                <Metin blok value={v.etiket} onChange={(x) => listeGuncelle('varsayimlar', i, 'etiket', x)} kilit={kilit} />
                              </td>
                              <td className="px-3 py-2 text-gray-800">
                                <Metin blok value={v.deger} onChange={(x) => listeGuncelle('varsayimlar', i, 'deger', x)} kilit={kilit} />
                              </td>
                              <td className="px-3 py-2">
                                <span className={`inline-block align-middle px-2 py-0.5 rounded-md border text-[10px] font-medium whitespace-nowrap ${KAYNAK_STIL[v.kaynak] || 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                  <Metin value={v.kaynak} onChange={(x) => listeGuncelle('varsayimlar', i, 'kaynak', x)} kilit={kilit} />
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                <Metin blok value={v.not} onChange={(x) => listeGuncelle('varsayimlar', i, 'not', x)} kilit={kilit} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Bolum>
                )}

                {/* ── 7. TIMELINE ── */}
                {plan.takvim?.length > 0 && (
                  <Bolum no={plan.aylikDagilim?.length > 0 ? 7 : 6} baslik="Flighting & Timeline">
                    <div className="grid gap-3 md:grid-cols-2">
                      {plan.takvim.map((t, i) => (
                        <div key={i} className="border rounded-xl p-3.5">
                          <div className="text-[11px] font-bold text-gray-800 mb-1.5">
                            <Metin value={t.donem} onChange={(v) => listeGuncelle('takvim', i, 'donem', v)} kilit={kilit} />
                          </div>
                          <ul className="space-y-1">
                            {(t.maddeler || []).map((m, j) => (
                              <li key={j} className="text-[11px] text-gray-600 flex gap-1.5">
                                <span className="text-gray-300 select-none">•</span>
                                <Metin blok value={m} kilit={kilit}
                                  onChange={(v) => guncelle((p) => { p.takvim[i].maddeler[j] = v; return p; })} />
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </Bolum>
                )}

                {/* ── 8. ACTION ITEMS ── */}
                {plan.riskler?.length > 0 && (
                  <Bolum no={plan.aylikDagilim?.length > 0 ? 8 : 7} baslik="Action Items & Risks">
                    <div className="space-y-2">
                      {plan.riskler.map((r, i) => (
                        <div key={i} className="flex gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                          <span className="text-red-500 font-bold text-[11px] select-none">!</span>
                          <div className="text-[11px] text-red-800 leading-relaxed flex-1">
                            <Metin blok value={r} kilit={kilit}
                              onChange={(v) => guncelle((p) => { p.riskler[i] = v; return p; })} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Bolum>
                )}

                {uretim && (
                  <p className="text-[10px] text-gray-400 pt-2 border-t">
                    Plan generated on {new Date(uretim.uretimZamani).toLocaleDateString('en-GB')} with {uretim.model};
                    figures may have been edited manually. Impressions and clicks are derived from budget / CPM / CPC and are estimates.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── küçük bileşenler ──────────────────────────── */

// 1.234.567 → 1.23M · sunum kartlarında uzun sayı kutuyu taşırıyor.
function kisalt(n) {
  const v = say(n);
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e4) return `${Math.round(v / 1e3)}K`;
  return fmt(v);
}

const KPI_RENK = {
  slate:   'border-slate-200 bg-slate-50 text-slate-700',
  violet:  'border-violet-200 bg-violet-50 text-violet-700',
  sky:     'border-sky-200 bg-sky-50 text-sky-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  amber:   'border-amber-200 bg-amber-50 text-amber-700',
  rose:    'border-rose-200 bg-rose-50 text-rose-700',
};

function Kpi({ etiket, deger, alt, renk: r = 'slate' }) {
  return (
    <div className={`border rounded-xl p-3 ${KPI_RENK[r] || KPI_RENK.slate}`}>
      <div className="text-[10px] opacity-70">{etiket}</div>
      <div className="text-xl font-bold mt-0.5 leading-tight">{deger}</div>
      {alt && <div className="text-[10px] opacity-60 mt-0.5">{alt}</div>}
    </div>
  );
}

function Bolum({ no, baslik, children }) {
  return (
    <section className="space-y-3">
      <h3 className="text-[12px] font-bold text-gray-700 uppercase tracking-[0.08em]">
        {no}. {baslik}
      </h3>
      {children}
    </section>
  );
}

function Projeksiyon({ plan, ozet, pb, kilit, guncelle }) {
  const pr = plan.projeksiyon;
  if (!pr) return null;

  // Modelden gelmiyor, buradan türetiliyor — böylece bütçeyi değiştirince anında güncelleniyor.
  const siparis = Math.round(ozet.tiklama * (say(pr.donusumOrani) / 100));
  const ciro = Math.round(siparis * say(pr.sepetTutari));
  const roas = ozet.butce > 0 ? ciro / ozet.butce : 0;

  if (!pr.varMi) {
    return (
      <div className="p-3 rounded-xl bg-gray-50 border text-[11px] text-gray-600">
        <b>No projection provided.</b>{' '}
        <Metin blok value={pr.gerekce} kilit={kilit}
          onChange={(v) => guncelle((p) => { p.projeksiyon.gerekce = v; return p; })} />
        {!kilit && (
          <button className="mps-arac mt-2 block text-indigo-600 hover:underline"
            onClick={() => guncelle((p) => { p.projeksiyon.varMi = true; return p; })}>
            add a projection anyway
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="border rounded-xl p-3 bg-white">
          <div className="text-[10px] text-gray-500">Click → Sale</div>
          <div className="text-xl font-bold text-gray-800 mt-0.5">
            <Sayi value={pr.donusumOrani} basamak={2} arka="%" kilit={kilit}
              onChange={(v) => guncelle((p) => { p.projeksiyon.donusumOrani = v; return p; })} />
          </div>
        </div>
        <div className="border rounded-xl p-3 bg-white">
          <div className="text-[10px] text-gray-500">Avg. Order Value</div>
          <div className="text-xl font-bold text-gray-800 mt-0.5">
            <Sayi value={pr.sepetTutari} on={pb} kilit={kilit}
              onChange={(v) => guncelle((p) => { p.projeksiyon.sepetTutari = v; return p; })} />
          </div>
        </div>
        <Kpi renk="sky"     etiket="Orders"  deger={fmt(siparis)} alt="clicks × rate" />
        <Kpi renk="emerald" etiket="Revenue" deger={`${pb}${kisalt(ciro)}`} alt="orders × AOV" />
        <Kpi renk="amber"   etiket="ROAS"    deger={`${fmt(roas, 2)}×`} alt="revenue ÷ budget" />
      </div>
      {(pr.not || !kilit) && (
        <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
          <Metin blok value={pr.not} kilit={kilit}
            onChange={(v) => guncelle((p) => { p.projeksiyon.not = v; return p; })} />
        </p>
      )}
    </>
  );
}

/* Düzenlenebilir alanların görsel işareti. Temiz modda tamamen kalkar ki
   ekran görüntüsünde kesik çizgi / vurgu izi kalmasın. */
function StilBlogu() {
  return (
    <style>{`
      .mps-alan { outline: none; }
      .mps-alan:empty::before { content: attr(data-ipucu); color: #cbd5e1; }
      .mps-temiz .mps-alan { border-bottom: none !important; background: transparent !important; }
      .mps-temiz .mps-arac { display: none !important; }
      :not(.mps-temiz) .mps-alan {
        border-bottom: 1px dashed rgba(99, 102, 241, 0.35);
        border-radius: 2px;
        transition: background-color .12s ease;
      }
      :not(.mps-temiz) .mps-alan:hover { background-color: rgba(99, 102, 241, 0.07); }
      :not(.mps-temiz) .mps-alan:focus {
        background-color: rgba(99, 102, 241, 0.12);
        border-bottom-color: #4f46e5;
      }
      .mps-sayi { min-width: 1.2em; text-align: inherit; }
      .mps-koyu { border-bottom-color: rgba(255,255,255,0.45) !important; }
      .mps-koyu:hover, .mps-koyu:focus { background-color: rgba(255,255,255,0.15) !important; }
    `}</style>
  );
}
